import { useMutation, useQuery } from "@tanstack/react-query";
import { useAuth, authorizedAxios } from "@moonscape/shared";

interface Feature {
  name: string;
  description: string;
}

interface PlanFeatures {
  main: Feature[];
//   tax: Feature[];
//   sync: Feature[];
}

interface Plan {
  id: number;
  name: string;
  maxTransactions: string;
  price: number;
  selectPlanButton: boolean;
  features: {
    main: (boolean | string)[];
    // tax: boolean[];
    // sync: boolean[];
  };
}

const FEATURES: PlanFeatures = {
  main:  [
    {
      name: "Exchange Connections",
      description: "Sync trades from exchanges, wallets, and blockchains.",
    },
    {
      name: "Transaction Limit",
      description: "Max number of transactions you can import and process.",
    },
    {
      name: "Portfolio Tracking",
      description: "Track your crypto portfolio across 850+ platforms.",
    },
    {
      name: "Tax Engine",
      description: "Auto-calculate gains, losses, and crypto taxes.",
    },
    {
      name: "Accounting Methods",
      description: "Use FIFO, LIFO, HIFO, or Share Pooling.",
    },
    {
      name: "Tax Optimization",
      description: "Find trades to minimize your tax bill.",
    },
    {
      name: "Multi-year History",
      description: "Access reports for multiple tax years.",
    },
    {
      name: "Export Options",
      description: "Export reports in CSV, XLSX, or PDF.",
    },
    {
      name: "Support",
      description: "Get help from our crypto tax experts.",
    },
  ],
//   tax: [
//     {
//       name: "Portfolio tracking",
//       description:
//         "Monitor and analyze your portfolio performance across multiple platforms.",
//     },
//     {
//       name: "Tax optimizer",
//       description:
//         "Leverage tools to optimize your taxes and increase efficiency.",
//     },
//     {
//       name: "DeFi & NFT support",
//       description:
//         "Seamlessly manage taxes for DeFi and NFT transactions with precision.",
//     },
//     {
//       name: "FIFO, LIFO, Average Cost & more",
//       description:
//         "Select the inventory method that best fits your tax strategy.",
//     },
//     {
//       name: "Wallet-based cost tracking (Spec ID)",
//       description:
//         "Enable wallet-specific cost tracking for accurate asset valuation.",
//     },
//     {
//       name: "Form 8949, Schedule D, K4, RF1159, Capital Gain Summary",
//       description:
//         "Automatically generate tax forms required for filing in various jurisdictions.",
//     },
//     {
//       name: "Export to TurboTax, TaxACT",
//       description:
//         "Easily export your tax data to TurboTax, TaxACT, and other platforms.",
//     },
//     {
//       name: "20,000+ cryptocurrencies in CEX",
//       description:
//         "Track and manage over 20,000 cryptocurrencies from centralized exchanges.",
//     },
//     {
//       name: "300,000+ cryptocurrencies in DEX",
//       description:
//         "Support for tracking over 300,000 cryptocurrencies from decentralized exchanges.",
//     },
//     {
//       name: "Mining, Staking, Lending, Airdrops As Income",
//       description:
//         "Classify and report income from mining, staking, lending, and airdrops.",
//     },
//   ],
//   sync: [
//     {
//       name: "Exchange API sync",
//       description:
//         "Synchronize your exchange accounts automatically using secure APIs.",
//     },
//     {
//       name: "CSV file import",
//       description:
//         "Import transactions directly from CSV files for quick integration.",
//     },
//     {
//       name: "EVM chains auto-sync",
//       description:
//         "Automatically sync data from Ethereum Virtual Machine (EVM) chains.",
//     },
//     {
//       name: "Solana, Cardano & others",
//       description:
//         "Support for syncing transactions from Solana, Cardano, and other major blockchains.",
//     },
//     {
//       name: "xPub/yPub/zPub For Bitcoin",
//       description:
//         "Enable extended public key tracking for Bitcoin and related assets.",
//     },
//     {
//       name: "Coinbase, Binance, Kucoin & others",
//       description:
//         "Seamlessly sync with popular exchanges like Coinbase, Binance, and Kucoin.",
//     },
//     {
//       name: "8+ years of historical spot prices",
//       description:
//         "Access a database of historical spot prices spanning over 8 years.",
//     },
//   ],
};

const PLANS: Plan[] = [
  {
    id: 1,
    name: "Standard",
    maxTransactions: "50000",
    price: 49,
    selectPlanButton: true,
    features: {
      main: ["Unlimited", "50,000 soft cap", "Full", true, "FIFO, LIFO, HIFO, Share Pooling", "Tax Loss Harvesting", true, "All formats + TurboTax", "Email (<24h)"],
    },
  },
  {
    id: 2,
    name: "Concierge",
    maxTransactions: "50000+",
    selectPlanButton: true,
    price: 499,
    features: {
      main: ["Unlimited", "50,000+ (custom)", "Full", true, "FIFO, LIFO, HIFO, Share Pooling", "Tax Loss Harvesting", true, "All formats + TurboTax", "Same-day (<4h)"],
    },
  },
];

interface FeatureListItem {
  plan: string;
  price: number;
  featureType: string;
  featureSubtype: string;
  description: string;
  value: boolean | string | number;
}

interface GroupedFeatures {
  [featureType: string]: {
    [featureSubtype: string]: FeatureListItem[];
  };
}
export const usePlan = ({
  onSuccess,
  onError,
  onSuccessCheckOut,
}: {
  onSuccess?: () => void;
  onError?: (err: unknown) => void;
  onSuccessCheckOut?: () => void;
}) => {
  const { getToken } = useAuth();

  const { refetch, isLoading, error, data } = useQuery({
    queryKey: ["plans"],
    queryFn: async () => {
      const token = await getToken();
      const response = await authorizedAxios(token).get("/plan/all");
      return response.data;
    },
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (err) => {
      console.error("[usePlan] Error fetching plans:", err);
      onError?.(err);
    },
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });

  const {
    mutate: createCheckoutSession,
    isPending: isCheckoutLoading,
    error: isCheckoutError,
  } = useMutation({
    mutationFn: async (planIdOrOptions: string | number | { planId: string | number; coupon?: string; origin?: string }) => {
      const token = await getToken();
      let planId: string | number;
      let coupon: string | undefined;
      let origin: string | undefined;

      if (typeof planIdOrOptions === 'string' || typeof planIdOrOptions === 'number') {
        planId = planIdOrOptions;
      } else {
        planId = planIdOrOptions.planId;
        coupon = planIdOrOptions.coupon;
        origin = planIdOrOptions.origin;
      }

      let url = `/stripe/create-checkout-session?planId=${planId}`;
      if (coupon) {
        url += `&coupon=${encodeURIComponent(coupon)}`;
      }
      if (origin) {
        url += `&origin=${encodeURIComponent(origin)}`;
      }

      const response = await authorizedAxios(token).get(url);
      return response.data; // { url: "https://...", id: "..." }
    },
    onSuccess: (data) => {
      onSuccessCheckOut?.(data);
    },
    onError: (err) => {
      console.error("[usePlan] Checkout error:", err);
      onError?.(err);
    },
  });

  return {
    refetch: refetch,
    isQueryloading: isLoading,
    error: error,
    data: data,
    PLANS,
    FEATURES,

    createCheckoutSession, // function to call with a planId
    isCheckoutLoading,
    isCheckoutError,
  };
};
